import { queryOptions } from '@tanstack/react-query';
import type { CouriersListFilter } from '../lib/filters';
import { courierAdminApi, type CourierId } from './courier-api';

/**
 * Query-key factory + query-options for the admin couriers domain. Mutations
 * invalidate through `courierAdminKeys` (never an inline array).
 */
export const courierAdminKeys = {
  all: ['admin-couriers'] as const,
  lists: () => [...courierAdminKeys.all, 'list'] as const,
  list: (filter: CouriersListFilter) => [...courierAdminKeys.lists(), filter] as const,
  equipments: () => [...courierAdminKeys.all, 'equipments'] as const,
  bonuses: () => [...courierAdminKeys.all, 'bonuses'] as const,
  fouls: () => [...courierAdminKeys.all, 'fouls'] as const,
  orders: (courierId: CourierId) =>
    [...courierAdminKeys.all, 'orders', courierId] as const,
  block: (courierId: CourierId) => [...courierAdminKeys.all, 'block', courierId] as const,
};

const OPTIONS_STALE_MS = 5 * 60 * 1000;

export const courierAdminQueries = {
  list: (filter: CouriersListFilter) =>
    queryOptions({
      queryKey: courierAdminKeys.list(filter),
      queryFn: () => courierAdminApi.list(filter),
      placeholderData: (prev) => prev,
    }),

  equipments: (enabled = true) =>
    queryOptions({
      queryKey: courierAdminKeys.equipments(),
      queryFn: () => courierAdminApi.equipments(),
      enabled,
      staleTime: OPTIONS_STALE_MS,
    }),

  bonuses: (enabled = true) =>
    queryOptions({
      queryKey: courierAdminKeys.bonuses(),
      queryFn: () => courierAdminApi.bonuses(),
      enabled,
      staleTime: OPTIONS_STALE_MS,
    }),

  fouls: (enabled = true) =>
    queryOptions({
      queryKey: courierAdminKeys.fouls(),
      queryFn: () => courierAdminApi.fouls(),
      enabled,
      staleTime: OPTIONS_STALE_MS,
    }),

  orders: (courierId: CourierId, enabled = true) =>
    queryOptions({
      queryKey: courierAdminKeys.orders(courierId),
      queryFn: () => courierAdminApi.courierOrders(courierId),
      enabled,
    }),

  /** Lazy: only fetched when the blocked-status hint is opened. */
  latestBlock: (courierId: CourierId, enabled = false) =>
    queryOptions({
      queryKey: courierAdminKeys.block(courierId),
      queryFn: () => courierAdminApi.latestBlock(courierId),
      enabled,
    }),
};
